import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {JobRead, JobStatus, JobType} from "../schemas";
import {useGroupContext} from "./group_context";
import {
    DOCUMENT_UPLOAD_COMPLETED,
    DocumentUploadCompletedEventDetail,
    INVESTIGATION_COMPLETED,
    InvestigationCompletedEventDetail,
    JOB_ADDED,
    JobAddedEventDetail,
    PRODUCTION_COMPLETED,
    ProductionCompletedEventDetail,
    SEED_COMPLETED,
    SeedCompletedEventDetail,
    TAG_UPDATE_COMPLETED,
    TagUpdatedEventDetail
} from "../events";
import {getActiveJobs} from "../api";
import {POLLING_REFRESH_RATE} from "../config";


export interface JobProviderContextType {
    activeJobs: JobRead[];
    setActiveJobs: React.Dispatch<React.SetStateAction<JobRead[]>>;
    addJobs: (jobs: JobRead[]) => void;
    dismissJob: (jobId: number) => void;
}

export const JobProviderContext = createContext<JobProviderContextType | undefined>(undefined);

interface JobProviderProps {
    children: ReactNode;
}

export const JobProvider: React.FC<JobProviderProps> = ({
                                                            children,
                                                        }) => {

    const {
        currentGroup,
    } = useGroupContext()!;

    const [activeJobs, setActiveJobs] = useState<JobRead[]>([]);
    const [shouldPoll, setShouldPoll] = useState(false);

    // Refs to avoid stale closures in interval callback
    const lastActiveJobsRef = useRef<JobRead[]>([]);
    const groupIdRef = useRef<string | null>(null);
    // Job IDs dismissed by retry — filtered out of poll results so the old
    // failed job doesn't reappear after the new job starts tracking.
    const dismissedJobIdsRef = useRef<Set<number>>(new Set());

    // Keep groupIdRef in sync
    useEffect(() => {
        groupIdRef.current = currentGroup?.id ?? null;
    }, [currentGroup?.id]);

    // Initialize active jobs when group changes
    useEffect(() => {
        if (currentGroup) {
            // Include non-completed jobs AND completed/failed jobs with warnings
            const initialActiveJobs = currentGroup.jobs.filter(job =>
                job.status !== JobStatus.COMPLETED ||
                (job.warnings && job.warnings.length > 0)
            );
            setActiveJobs(initialActiveJobs);
            lastActiveJobsRef.current = initialActiveJobs;

            // If there are already active jobs, start polling
            if (initialActiveJobs.length > 0) {
                setShouldPoll(true);
            }
        }
    }, [currentGroup?.id]);

    // Listen for JOB_ADDED events to trigger polling
    useEffect(() => {
        const handleJobAdded = () => {
            setShouldPoll(true);
        };

        window.addEventListener(JOB_ADDED, handleJobAdded as EventListener);
        return () => {
            window.removeEventListener(JOB_ADDED, handleJobAdded as EventListener);
        };
    }, []);

    // Polling effect - controlled by shouldPoll state
    useEffect(() => {
        const groupId = groupIdRef.current;
        if (!groupId || !shouldPoll) return;

        const poll = async () => {
            const currentGroupId = groupIdRef.current;
            if (!currentGroupId) {
                setShouldPoll(false);
                return;
            }

            let rawPolledJobs: JobRead[];
            try {
                rawPolledJobs = await getActiveJobs(currentGroupId);
            } catch {
                // Silently skip transient network errors (Cloud Run cold starts,
                // tab throttling, brief connectivity loss). The next poll will retry.
                return;
            }

            // Filter out jobs dismissed by retry
            const polledJobs = rawPolledJobs.filter(j => !dismissedJobIdsRef.current.has(j.id));

            // Jobs that disappeared from the poll AND don't have warnings are completed.
            // Jobs with warnings stay visible (backend keeps returning them) for dismissal.
            const completedJobs = lastActiveJobsRef.current.filter(
                job => !polledJobs.some(p => p.id === job.id) &&
                    !(job.warnings && job.warnings.length > 0)
            );

            lastActiveJobsRef.current = polledJobs;
            setActiveJobs(polledJobs);

            // Dispatch completion events
            for (const job of completedJobs) {
                if (job.type === JobType.TAG_UPDATE) {
                    window.dispatchEvent(new CustomEvent<TagUpdatedEventDetail>(TAG_UPDATE_COMPLETED, {
                        detail: {tagIds: job.updated_ids}
                    }));
                }
                if (job.type === JobType.UPLOAD) {
                    window.dispatchEvent(new CustomEvent<DocumentUploadCompletedEventDetail>(DOCUMENT_UPLOAD_COMPLETED, {
                        detail: {documentIds: job.updated_ids}
                    }));
                }
                if (job.type === JobType.PRODUCTION) {
                    window.dispatchEvent(new CustomEvent<ProductionCompletedEventDetail>(PRODUCTION_COMPLETED, {
                        detail: {productionIds: job.updated_ids}
                    }));
                }
                if (job.type === JobType.INVESTIGATION) {
                    window.dispatchEvent(new CustomEvent<InvestigationCompletedEventDetail>(INVESTIGATION_COMPLETED, {
                        detail: {groupId: job.group_id, tagIds: job.updated_ids}
                    }));
                }
                if (job.type === JobType.SEED) {
                    window.dispatchEvent(new CustomEvent<SeedCompletedEventDetail>(SEED_COMPLETED, {
                        detail: {groupId: job.group_id}
                    }));
                }
            }

            // Stop polling if no jobs are still in progress (terminal jobs stay visible but don't need updates)
            const hasInProgressJobs = polledJobs.some(
                j => j.status === JobStatus.QUEUED || j.status === JobStatus.PROCESSING
            );
            if (!hasInProgressJobs) {
                setShouldPoll(false);
            }
        };

        const interval = setInterval(poll, POLLING_REFRESH_RATE);

        // Also poll immediately
        poll();

        return () => clearInterval(interval);
    }, [shouldPoll]);

    const addJobs = useCallback((jobs: JobRead[]) => {
        // Dispatch our custom event so anyone can listen
        for (const job of jobs)
            window.dispatchEvent(new CustomEvent<JobAddedEventDetail>(JOB_ADDED, {detail: {job}}));
        setActiveJobs(prev => {
            const updated = [...prev, ...jobs];
            lastActiveJobsRef.current = updated;
            return updated;
        });
    }, []);

    const dismissJob = useCallback((jobId: number) => {
        dismissedJobIdsRef.current.add(jobId);
        setActiveJobs(prev => {
            const updated = prev.filter(j => j.id !== jobId);
            lastActiveJobsRef.current = updated;
            return updated;
        });
    }, []);

    const contextValue: JobProviderContextType = useMemo(() => ({
        activeJobs: activeJobs,
        setActiveJobs: setActiveJobs,
        addJobs: addJobs,
        dismissJob: dismissJob,
    }), [activeJobs, addJobs, dismissJob]);

    return (
        <JobProviderContext.Provider value={contextValue}>
            {children}
        </JobProviderContext.Provider>
    );
};

export const useJobContext = () => useContext(JobProviderContext);
